import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Save, Trash2, User, Building2, Home } from "lucide-react";
import { toast } from "sonner";
import ImageDropField from "../components/ImageDropField";

const RW_LIST = ["01","02","03","04","05","06","07","08","09","10"];

const FIELD_CLS = "w-full h-11 px-3 rounded-lg border border-[hsl(var(--border))] bg-white text-sm text-[#0e2b26] focus:outline-none focus:ring-2 focus:ring-[#0e2b26]/30 focus:border-transparent";

function defaultJabatan(scope, rw, rt) {
  if (scope === "kelurahan") return "Lurah Suka Asih";
  if (scope === "rw") return `Ketua RW ${rw}`;
  return `Ketua RT ${rt}`;
}

function getAsetLabel(a) {
  if (a.scope === "kelurahan") return "Kelurahan";
  if (a.scope === "rw") return `RW ${a.rw}`;
  return `RW ${a.rw} / RT ${a.rt}`;
}

function AsetForm({ scope, rw, rt, initial, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    nama: initial?.nama || "",
    nip: initial?.nip || "",
    jabatan: initial?.jabatan || defaultJabatan(scope, rw, rt),
    url_ttd: initial?.url_ttd || "",
    url_stempel: initial?.url_stempel || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!initial) return;
    setForm({
      nama: initial.nama || "",
      nip: initial.nip || "",
      jabatan: initial.jabatan || defaultJabatan(scope, rw, rt),
      url_ttd: initial.url_ttd || "",
      url_stempel: initial.url_stempel || "",
    });
  }, [initial, scope, rw, rt]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.masterAsetPut({
        scope,
        rw: scope !== "kelurahan" ? String(rw).padStart(2, "0") : null,
        rt: scope === "rt" ? String(rt).padStart(3, "0") : null,
        ...form,
      });
      toast.success("Aset berhasil disimpan");
      onSaved && onSaved();
    } catch { toast.error("Gagal menyimpan aset"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!initial) return;
    setSaving(true);
    try {
      await api.masterAsetDelete({
        scope,
        rw: scope !== "kelurahan" ? String(rw).padStart(2, "0") : undefined,
        rt: scope === "rt" ? String(rt).padStart(3, "0") : undefined,
      });
      toast.success("Aset dihapus");
      onDeleted && onDeleted();
    } catch { toast.error("Gagal menghapus"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="block text-[0.7rem] font-bold tracking-wider text-[hsl(var(--muted-foreground))] uppercase mb-1">Nama</label>
        <input className={FIELD_CLS} value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="................" data-testid={`aset-nama-${scope}-${rw||""}-${rt||""}`} />
      </div>
      <div>
        <label htmlFor={`aset-nip-${scope}-${rw||""}-${rt||""}`} className="block text-[0.7rem] font-bold tracking-wider text-[hsl(var(--muted-foreground))] uppercase mb-1">NIP</label>
        <input
          id={`aset-nip-${scope}-${rw||""}-${rt||""}`}
          name="nip"
          type="text"
          autoComplete="off"
          className={FIELD_CLS}
          value={form.nip}
          onChange={(e) => setForm({ ...form, nip: e.target.value })}
          data-testid={`aset-nip-${scope}-${rw||""}-${rt||""}`}
        />
      </div>
      <div>
        <label className="block text-[0.7rem] font-bold tracking-wider text-[hsl(var(--muted-foreground))] uppercase mb-1">Jabatan</label>
        <input className={FIELD_CLS} value={form.jabatan} onChange={(e) => setForm({ ...form, jabatan: e.target.value })} />
      </div>
      <ImageDropField
        label="Tanda Tangan"
        value={form.url_ttd}
        onChange={(v) => setForm({ ...form, url_ttd: v })}
        testId={`ttd-drop-${scope}-${rw||""}-${rt||""}`}
      />
      <ImageDropField
        label="Stempel"
        value={form.url_stempel}
        onChange={(v) => setForm({ ...form, url_stempel: v })}
        testId={`stempel-drop-${scope}-${rw||""}-${rt||""}`}
      />
      <div className="sm:col-span-2 flex flex-wrap gap-2 mt-1">
        <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-[#0e2b26] text-white text-sm font-semibold hover:bg-[#123832] disabled:opacity-50" data-testid={`save-${scope}-${rw||""}-${rt||""}`}>
          <Save size={14} /> Simpan
        </button>
        {initial && (
          <button onClick={del} disabled={saving} className="inline-flex items-center gap-2 px-3 h-10 rounded-lg border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50">
            <Trash2 size={14} /> Hapus
          </button>
        )}
      </div>
    </div>
  );
}

export default function MasterAset() {
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("kelurahan"); // kelurahan | rw | rt
  const [selectedRw, setSelectedRw] = useState("01");
  const [selectedRt, setSelectedRt] = useState("001");
  const [rtOptions, setRtOptions] = useState([]);

  const refresh = async () => {
    try { const r = await api.masterAsetList(); setList(r.data || []); }
    catch { setList([]); }
  };
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (tab === "rt" && selectedRw) {
      api.listRt(selectedRw).then((r) => {
        setRtOptions(r.data || []);
        if (!(r.data || []).find((x) => x.rt === selectedRt)) {
          setSelectedRt(r.data?.[0]?.rt || "001");
        }
      });
    }
  }, [tab, selectedRw, selectedRt]);

  const findAset = (scope, rw, rt) =>
    list.find((a) => a.scope === scope
      && (rw ? a.rw === String(rw).padStart(2, "0") : !a.rw)
      && (rt ? a.rt === String(rt).padStart(3, "0") : !a.rt));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0e2b26] tracking-tight">Master Aset TTD & Stempel</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Kelola nama pejabat, URL tanda tangan, dan URL stempel untuk dokumen A.5.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-[hsl(var(--border))] p-1 flex gap-1">
        {[
          { k: "kelurahan", label: "Lurah Suka Asih", icon: Building2 },
          { k: "rw", label: "Ketua RW", icon: Home },
          { k: "rt", label: "Ketua RT", icon: User },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`flex-1 h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition ${tab === t.k ? "bg-[#0e2b26] text-white" : "text-[#0e2b26] hover:bg-[hsl(var(--muted))]"}`}
            data-testid={`tab-${t.k}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "kelurahan" && (
        <div className="bg-white rounded-xl border border-[hsl(var(--border))] p-5">
          <h3 className="font-bold text-[#0e2b26] mb-3">Penandatangan Kelurahan Suka Asih</h3>
          <AsetForm scope="kelurahan" initial={findAset("kelurahan", null, null)} onSaved={refresh} onDeleted={refresh} />
        </div>
      )}

      {tab === "rw" && (
        <div className="bg-white rounded-xl border border-[hsl(var(--border))] p-5 space-y-4">
          <div>
            <label className="block text-[0.7rem] font-bold tracking-wider text-[hsl(var(--muted-foreground))] uppercase mb-1">Pilih RW</label>
            <select className={FIELD_CLS + " max-w-xs"} value={selectedRw} onChange={(e) => setSelectedRw(e.target.value)}>
              {RW_LIST.map((r) => <option key={r} value={r}>RW {r}</option>)}
            </select>
          </div>
          <div className="border-t pt-4">
            <h3 className="font-bold text-[#0e2b26] mb-3">Aset Ketua RW {selectedRw}</h3>
            <AsetForm key={`rw-${selectedRw}`} scope="rw" rw={selectedRw} initial={findAset("rw", selectedRw, null)} onSaved={refresh} onDeleted={refresh} />
          </div>
        </div>
      )}

      {tab === "rt" && (
        <div className="bg-white rounded-xl border border-[hsl(var(--border))] p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div>
              <label className="block text-[0.7rem] font-bold tracking-wider text-[hsl(var(--muted-foreground))] uppercase mb-1">RW</label>
              <select className={FIELD_CLS} value={selectedRw} onChange={(e) => setSelectedRw(e.target.value)}>
                {RW_LIST.map((r) => <option key={r} value={r}>RW {r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[0.7rem] font-bold tracking-wider text-[hsl(var(--muted-foreground))] uppercase mb-1">RT</label>
              <select className={FIELD_CLS} value={selectedRt} onChange={(e) => setSelectedRt(e.target.value)}>
                {rtOptions.map((r) => <option key={r.rt} value={r.rt}>RT {r.rt}</option>)}
              </select>
            </div>
          </div>
          <div className="border-t pt-4">
            <h3 className="font-bold text-[#0e2b26] mb-3">Aset Ketua RT {selectedRt} / RW {selectedRw}</h3>
            <AsetForm key={`rt-${selectedRw}-${selectedRt}`} scope="rt" rw={selectedRw} rt={selectedRt} initial={findAset("rt", selectedRw, selectedRt)} onSaved={refresh} onDeleted={refresh} />
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-[hsl(var(--border))] p-5">
        <h3 className="font-bold text-[#0e2b26] mb-3">Aset Terdaftar ({list.length})</h3>
        {list.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Belum ada aset tersimpan.</p>
        ) : (
          <div className="space-y-2 text-sm">
            {list.map((a) => (
              <div key={`${a.scope}-${a.rw || "-"}-${a.rt || "-"}`} className="flex items-center justify-between border-b pb-2">
                <div>
                  <div className="font-semibold text-[#0e2b26]">
                    {getAsetLabel(a)}
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{a.nama || "-"} · {a.jabatan || "-"}</div>
                </div>
                <div className="flex gap-2 text-xs">
                  {a.url_ttd && <span className="badge-pill badge-sudah">TTD</span>}
                  {a.url_stempel && <span className="badge-pill badge-sudah">Stempel</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
